import { useState } from 'react';
import { Alert, Button, DestinationGroup, Modal, ModalActions } from '@evogard/ui';
import type { AnalysisForm } from '@evogard/contract';
import {
  ANALYSIS_POSSESSION,
  claimClosureKindOf,
  isVehicleInYard,
  possessionOutcomeOf,
  type CatalogFormContext,
  type CatalogFormState,
} from '@evogard/domain';
import { formStateOf, type Desk } from './desk.js';
import { PhaseForm } from './PhaseForm.js';
import { useDeskForm, useDeskMutations } from './useDesk.js';
import type { LastMove } from './UndoMove.js';

/**
 * O negado com posse (16/10/2026): a negativa com o veículo numa base da
 * empresa, que o associado ainda vai buscar. Não é fase — é uma parada de
 * Negativas a Enviar (`ANALYSIS_POSSESSION`), como o pedido de desistência:
 *
 * - `PossessionRequest` oferece a parada quando "Veículo está em base?" é Sim.
 *   Parar não pede texto;
 * - `PossessionClose` encerra: o modal com o formulário que o pipe tinha em
 *   "Negados com Posse", e a resposta de "Veículo devolvido" escolhe o
 *   destino — devolvido, Negado; recusa do associado, Negados com Posse
 *   Jurídico. A API confere as duas coisas de novo.
 */

/** Oferece a parada, em Negativas a Enviar, com o veículo em base. */
export function PossessionRequest({
  desk,
  claimId,
  phase,
  form,
  state,
  draft,
  onSaved,
  enabled,
}: {
  desk: Desk;
  claimId: number;
  phase: string;
  form: AnalysisForm | undefined;
  state: CatalogFormState;
  /** O rascunho da fase: o "Sim" ainda não gravado vai junto, antes de parar. */
  draft: Readonly<Record<string, string | null>>;
  onSaved: () => void;
  enabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mutations = useDeskMutations(desk, claimId);
  if (
    !enabled ||
    desk.department !== 'analise' ||
    form?.hold ||
    !isVehicleInYard(phase, state)
  ) {
    return null;
  }
  const busy = mutations.save.isPending || mutations.hold.isPending;

  const stop = async () => {
    setError(null);
    try {
      if (Object.keys(draft).length > 0) {
        await mutations.save.mutateAsync({ phase, values: { ...draft } });
        onSaved();
      }
      await mutations.hold.mutateAsync({
        fromPhase: phase,
        type: ANALYSIS_POSSESSION.hold,
      });
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível parar o sinistro.');
    }
  };

  return (
    <>
      <DestinationGroup
        title="Veículo em base"
        direction="forward"
        destinations={[{ id: ANALYSIS_POSSESSION.hold, name: 'Negado com posse' }]}
        selected={null}
        onSelect={() => setOpen(true)}
      />
      {open && (
        <Modal
          open
          onOpenChange={(o) => !o && !busy && setOpen(false)}
          title="Negado com posse"
          descricao={`O sinistro fica parado em ${desk.phaseOf(phase)?.label ?? phase}, com a etiqueta, até o associado buscar o veículo na base. Ao encerrar, a mesa pede o que falta e o envia para Negado ou para Negados com Posse Jurídico.`}
          icon="veiculo"
          tone="destaque"
          size="sm"
        >
          <div className="flex flex-col gap-4">
            {error && <Alert tone="erro">{error}</Alert>}
            <ModalActions>
              <Button variant="secundario" disabled={busy} onClick={() => setOpen(false)}>
                Cancelar
              </Button>
              <Button loading={busy} onClick={() => void stop()}>
                Parar o sinistro
              </Button>
            </ModalActions>
          </div>
        </Modal>
      )}
    </>
  );
}

/** Parado no negado com posse: o modal que encerra. */
export function PossessionClose({
  desk,
  claimId,
  context,
  phase,
  phaseDraft,
  onSaved,
  onMoved,
  enabled,
}: {
  desk: Desk;
  claimId: number;
  context: CatalogFormContext;
  /** A fase onde o sinistro está parado — Negativas a Enviar. */
  phase: string;
  /** O rascunho dela: encerrar é avançar, e ela precisa estar gravada. */
  phaseDraft: Readonly<Record<string, string | null>>;
  onSaved: () => void;
  onMoved: (move: LastMove) => void;
  enabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!enabled) return null;
  return (
    <>
      <DestinationGroup
        title="Encerrar a negativa"
        direction="forward"
        destinations={[{ id: 'encerrar', name: 'Concluir o negado com posse' }]}
        selected={null}
        onSelect={() => setOpen(true)}
      />
      {open && (
        <PossessionCloseModal
          desk={desk}
          claimId={claimId}
          context={context}
          phase={phase}
          phaseDraft={phaseDraft}
          onSaved={onSaved}
          onMoved={onMoved}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function PossessionCloseModal({
  desk,
  claimId,
  context,
  phase,
  phaseDraft,
  onSaved,
  onMoved,
  onClose,
}: {
  desk: Desk;
  claimId: number;
  context: CatalogFormContext;
  phase: string;
  phaseDraft: Readonly<Record<string, string | null>>;
  onSaved: () => void;
  onMoved: (move: LastMove) => void;
  onClose: () => void;
}) {
  const formPhase = ANALYSIS_POSSESSION.form;
  const form = useDeskForm(desk, claimId, formPhase);
  const mutations = useDeskMutations(desk, claimId);
  const [draft, setDraft] = useState<Record<string, string | null>>({});
  const [marked, setMarked] = useState<ReadonlySet<string>>(new Set());
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const state = formStateOf(form.data, draft);
  const missing = desk.missingFields(formPhase, state, context);
  const outcome = possessionOutcomeOf(state);
  const outcomeLabel = outcome ? (desk.phaseOf(outcome)?.label ?? outcome) : null;
  const busy = mutations.save.isPending || mutations.move.isPending;

  const close = async () => {
    setError(null);
    if (missing.length > 0 || !outcome) {
      setMarked(
        new Set(missing.map((f) => f.key).filter((k): k is string => Boolean(k))),
      );
      return;
    }
    try {
      if (Object.keys(phaseDraft).length > 0) {
        await mutations.save.mutateAsync({ phase, values: { ...phaseDraft } });
        onSaved();
      }
      if (Object.keys(draft).length > 0) {
        await mutations.save.mutateAsync({ phase: formPhase, values: draft });
      }
      const moved = await mutations.move.mutateAsync({
        fromPhase: phase,
        toPhase: outcome,
      });
      onClose();
      onMoved({
        claimId,
        department: desk.department,
        toLabel: outcomeLabel!,
        passageId: moved.passage.id,
        closed: claimClosureKindOf(desk.department, outcome) !== null,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível encerrar o sinistro.');
    }
  };

  return (
    <Modal
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title="Concluir o negado com posse"
      descricao={
        'A resposta de "Veículo devolvido" decide o destino: devolvido vai a Negado; recusa do associado, a Negados com Posse Jurídico.'
      }
      icon="veiculo"
      tone="destaque"
      size="md"
    >
      <div className="flex flex-col gap-4">
        {form.isLoading ? (
          <p className="text-sm text-texto-suave">Carregando o formulário…</p>
        ) : form.isError ? (
          <Alert tone="erro">{form.error.message}</Alert>
        ) : form.data ? (
          <PhaseForm
            desk={desk}
            claimId={claimId}
            claimType={context.claimType}
            claimEvent={context.claimEvent}
            phase={formPhase}
            form={form.data}
            draft={draft}
            state={state}
            marked={marked}
            readOnlyNotice={false}
            onChange={(key, value) => {
              setDraft((d) => ({ ...d, [key]: value }));
              setMarked((m) => {
                if (!m.has(key)) return m;
                const next = new Set(m);
                next.delete(key);
                return next;
              });
            }}
            uploading={uploading}
            onAttach={(key, picked, types) => {
              setUploading(key);
              mutations.attach.mutate(
                {
                  phase: formPhase,
                  field: key,
                  files: picked.map((file, i) => ({ file, typeMime: types[i]! })),
                },
                {
                  onError: (e) => setError(e.message),
                  onSettled: () => setUploading(null),
                },
              );
            }}
            onArchive={(key, documentId) =>
              mutations.archiveFile.mutate(
                { phase: formPhase, field: key, documentId },
                { onError: (e) => setError(e.message) },
              )
            }
          />
        ) : null}
        {outcomeLabel && (
          <p className="text-sm text-texto-suave">
            Pela resposta de "Veículo devolvido", o sinistro vai para{' '}
            <strong className="text-texto">{outcomeLabel}</strong>.
          </p>
        )}
        {error && <Alert tone="erro">{error}</Alert>}
        <ModalActions>
          <Button variant="secundario" disabled={busy} onClick={onClose}>
            Cancelar
          </Button>
          <Button loading={busy} disabled={!form.data} onClick={() => void close()}>
            {outcomeLabel
              ? `Encerrar em ${outcomeLabel}`
              : 'Responda "Veículo devolvido"'}
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
}
